import React, { useEffect, useMemo, useRef, useState } from 'react';
import BetCard, { Bet } from './BetCard';
import './Dashboard.css';
import { fetchBets, fetchDistinctBookmakerTitles, fetchDistinctSportTitles } from '../services/analytics';

// Live data state
const DEFAULT_PAGE_SIZE = 30;

// Trends were moved to the dedicated /trends page

type SortField =
  | 'odds'
  | 'vig'
  | 'fairOdds'
  | 'impliedProbability'
  | 'trueWinProbability'
  | 'expectedValue'
  | 'marketLastUpdate';

const Dashboard: React.FC = () => {
  // Optional UX cleanup: clear temporary onboarding artifact
  useEffect(() => {
    try { localStorage.removeItem('subscriptionId') } catch (_) {}
  }, [])
  // Only upcoming bets remain on the dashboard
  const [filters, setFilters] = useState({
    sport: '',
    market: '',
    bookmaker: '',
    homeTeam: '',
    awayTeam: '',
    fromTime: '',
    toTime: '',
    evFilter: 'all'
  });
  const [bets, setBets] = useState<Bet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const [bookmakerOptions, setBookmakerOptions] = useState<string[]>([]);
  const [sportOptions, setSportOptions] = useState<string[]>([]);
  const [sortField, setSortField] = useState<SortField>('expectedValue');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const handleFilterChange = (e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
    // Reset paging when filters change
    setOffset(0);
  };

  // Load upcoming bets from Supabase
  useEffect(() => {
    let isCancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);

      const serverSort = (() => {
        const dir = sortDirection === 'asc' ? 'asc' : 'desc';
        switch (sortField) {
          case 'marketLastUpdate':
            return dir === 'asc' ? 'updated_asc' : 'updated_desc';
          case 'odds':
            return dir === 'asc' ? 'odds_asc' : 'odds_desc';
          case 'vig':
            return dir === 'asc' ? 'vig_asc' : 'vig_desc';
          case 'fairOdds':
            return dir === 'asc' ? 'fair_odds_asc' : 'fair_odds_desc';
          case 'impliedProbability':
            return dir === 'asc' ? 'implied_prob_asc' : 'implied_prob_desc';
          case 'trueWinProbability':
            return dir === 'asc' ? 'true_win_prob_asc' : 'true_win_prob_desc';
          case 'expectedValue':
            return dir === 'asc' ? 'expected_value_asc' : 'expected_value_desc';
          default:
            return 'commence_time_asc';
        }
      })();

      const { data, error } = await fetchBets({
        isLive: false,
        evFilter: filters.evFilter as any,
        // Align market filter to service when possible
        marketKey: filters.market === 'Moneyline' ? 'h2h' : filters.market === 'Spread' ? 'spreads' : filters.market === 'Total' ? 'totals' : undefined,
        sportTitles: filters.sport ? [filters.sport] : undefined,
        bookmakerKey: undefined, // optional: map from title if needed later
        bookmakerTitles: filters.bookmaker ? [filters.bookmaker] : undefined,
        homeTeam: filters.homeTeam,
        awayTeam: filters.awayTeam,
        fromTime: filters.fromTime ? new Date(filters.fromTime).toISOString() : undefined,
        toTime: filters.toTime ? new Date(filters.toTime).toISOString() : undefined,
        limit: DEFAULT_PAGE_SIZE,
        offset,
        sort: serverSort,
      });

      if (isCancelled) return;

      if (error) {
        setError(error);
        setBets([]);
        setHasMore(false);
      } else {
        // Reset vs append depending on offset, without stale closure on `bets`
        const page = (data ?? []) as Bet[];
        setBets((prev) => (offset === 0 ? page : [...prev, ...page]));
        setHasMore(page.length === DEFAULT_PAGE_SIZE);
      }
      setLoading(false);
    };

    load();

    return () => {
      isCancelled = true;
    };
  }, [
    filters.evFilter,
    filters.market,
    filters.bookmaker,
    filters.homeTeam,
    filters.awayTeam,
    filters.fromTime,
    filters.toTime,
    filters.sport,
    offset,
    sortField,
    sortDirection
  ]);

  // Load distinct bookmaker and sport titles for selectors
  useEffect(() => {
    let cancelled = false;
    const loadBookmakers = async () => {
      const { data, error } = await fetchDistinctBookmakerTitles();
      if (cancelled) return;
      if (!error) setBookmakerOptions(data);
    };
    const loadSports = async () => {
      const { data, error } = await fetchDistinctSportTitles();
      if (cancelled) return;
      if (!error) setSportOptions(data);
    };
    loadBookmakers();
    loadSports();
    return () => { cancelled = true; };
  }, []);

  const resetFilters = () => {
    setFilters({
      sport: '',
      market: '',
      bookmaker: '',
      homeTeam: '',
      awayTeam: '',
      fromTime: '',
      toTime: '',
      evFilter: 'all'
    });
    setOffset(0);
  };

  const handleSortFieldChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortField(e.target.value as SortField);
    setOffset(0);
  };

  const handleSortDirectionChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortDirection(e.target.value as 'asc' | 'desc');
    setOffset(0);
  };

  const displayedBets = useMemo(() => {
    const getValue = (bet: Bet): number | null => {
      switch (sortField) {
        case 'odds':
          return bet.odds;
        case 'vig':
          return bet.vig;
        case 'fairOdds':
          return bet.fairOdds;
        case 'impliedProbability':
          return bet.impliedProbability;
        case 'trueWinProbability':
          return bet.trueWinProbability;
        case 'expectedValue':
          return bet.expectedValue;
        case 'marketLastUpdate':
          return bet.marketLastUpdate ? new Date(bet.marketLastUpdate).getTime() : null;
        default:
          return null;
      }
    };

    const sorted = [...bets].sort((a, b) => {
      const va = getValue(a);
      const vb = getValue(b);

      const aIsNull = va === null || Number.isNaN(va as number);
      const bIsNull = vb === null || Number.isNaN(vb as number);

      if (aIsNull && bIsNull) return 0;
      if (aIsNull) return 1; // nulls last
      if (bIsNull) return -1;

      if ((va as number) === (vb as number)) {
        const at = new Date(a.commenceTime).getTime();
        const bt = new Date(b.commenceTime).getTime();
        if (at === bt) return 0;
        return at - bt; // tiebreaker by commence time ascending
      }
      return sortDirection === 'asc'
        ? (va as number) - (vb as number)
        : (vb as number) - (va as number);
    });

    return sorted;
  }, [bets, sortField, sortDirection]);

  // Infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!hasMore || loading) return;

    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting) {
        setOffset(prev => prev + DEFAULT_PAGE_SIZE);
      }
    }, {
      root: null,
      rootMargin: '0px 0px 400px 0px',
      threshold: 0.01
    });

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [hasMore, loading]);

  return (
    <div className="dashboard">
      <div className="container">
        <div className="dashboard-header">
          <h1>Dashboard</h1>
          <p>Advanced analytics and betting insights</p>
        </div>


        {(
          <div className="upcoming-bets">
            <div className="filters-section">
              <h3>Filters</h3>
              <div className="filters-grid">
                <div className="filter-group">
                  <label htmlFor="sport">Sport</label>
                  <select
                    id="sport"
                    name="sport"
                    value={filters.sport}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Sports</option>
                    {sportOptions.map(title => (
                      <option key={title} value={title}>{title}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="market">Market</label>
                  <select
                    id="market"
                    name="market"
                    value={filters.market}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Markets</option>
                    <option value="Moneyline">Moneyline</option>
                    <option value="Spread">Spread</option>
                    <option value="Total">Total</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="bookmaker">Bookmaker</label>
                  <select
                    id="bookmaker"
                    name="bookmaker"
                    value={filters.bookmaker}
                    onChange={handleFilterChange}
                  >
                    <option value="">All Bookmakers</option>
                    {bookmakerOptions.map(title => (
                      <option key={title} value={title}>{title}</option>
                    ))}
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="homeTeam">Home Team</label>
                  <input
                    type="text"
                    id="homeTeam"
                    name="homeTeam"
                    value={filters.homeTeam}
                    onChange={handleFilterChange}
                    placeholder="Search home team..."
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="awayTeam">Away Team</label>
                  <input
                    type="text"
                    id="awayTeam"
                    name="awayTeam"
                    value={filters.awayTeam}
                    onChange={handleFilterChange}
                    placeholder="Search away team..."
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="fromTime">Event Start (From)</label>
                  <input
                    type="datetime-local"
                    id="fromTime"
                    name="fromTime"
                    value={filters.fromTime}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="toTime">Event Start (To)</label>
                  <input
                    type="datetime-local"
                    id="toTime"
                    name="toTime"
                    value={filters.toTime}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="filter-group">
                  <label htmlFor="evFilter">Expected Value</label>
                  <select
                    id="evFilter"
                    name="evFilter"
                    value={filters.evFilter}
                    onChange={handleFilterChange}
                  >
                    <option value="all">All</option>
                    <option value="positive">Positive EV</option>
                    <option value="negative">Negative EV</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="sortField">Sort by</label>
                  <select
                    id="sortField"
                    name="sortField"
                    value={sortField}
                    onChange={handleSortFieldChange}
                  >
                    <option value="odds">Odds</option>
                    <option value="vig">Vig</option>
                    <option value="fairOdds">Fair Odds</option>
                    <option value="impliedProbability">Implied Win %</option>
                    <option value="trueWinProbability">True Win %</option>
                    <option value="expectedValue">Expected Value</option>
                    <option value="marketLastUpdate">Odds Last Updated</option>
                  </select>
                </div>

                <div className="filter-group">
                  <label htmlFor="sortDirection">Order</label>
                  <select
                    id="sortDirection"
                    name="sortDirection"
                    value={sortDirection}
                    onChange={handleSortDirectionChange}
                  >
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </select>
                </div>
              </div>
            </div>

            {error && (
              <div className="no-results">
                <p>Failed to load bets: {error}</p>
              </div>
            )}

            {/* Initial load state */}
            {loading && offset === 0 && (
              <div className="no-results">
                <p>Loading bets…</p>
              </div>
            )}

            {/* Keep existing grid mounted during pagination loads to avoid scroll jumps */}
            {!error && (bets.length > 0 || (loading && offset > 0)) && (
              <div className="bets-grid">
                {displayedBets.map(bet => (
                  <BetCard key={bet.id} bet={bet} showAnalytics={true} />
                ))}
              </div>
            )}

            {!loading && !error && bets.length === 0 && (
              <div className="no-results">
                <p>No bets match your current filters.</p>
                <button
                  className="btn btn-secondary"
                  onClick={resetFilters}
                >
                  Clear Filters
                </button>
              </div>
            )}

            {!loading && !error && bets.length > 0 && hasMore && (
              <div className="no-results">
                <button
                  className="btn btn-secondary"
                  onClick={() => setOffset(prev => prev + DEFAULT_PAGE_SIZE)}
                >
                  Load More
                </button>
              </div>
            )}
            {/* Bottom loading indicator for pagination loads */}
            {loading && offset > 0 && (
              <div className="no-results">
                <p>Loading more…</p>
              </div>
            )}
            {/* Sentinel for infinite scroll */}
            <div ref={sentinelRef} />
          </div>
        )}

        {/* Trends moved to /trends */}
      </div>
    </div>
  );
};

export default Dashboard; 